import { useRef, useState } from 'react';
import { IconColor, IconID } from '@common/Icon/Icon';
import { IconButton } from '@common/IconButton/IconButton';
import { Section } from '@product/Section/Section';
import { TextInput } from '@common/TextInput/TextInput';
import { useTranslation } from 'next-i18next';
import type { ProductAttribute, ProductAttributeUnion, LabeledAttribute } from '@libs/attribute.helper';

type ChemicalSearchProps = {
  chemicalAttributes: string[];
  productAttributes: { [key: string]: ProductAttribute<ProductAttributeUnion> };
};

export const ChemicalSearch = ({ chemicalAttributes, productAttributes }: ChemicalSearchProps) => {
  const { t } = useTranslation(['product/productDetails', 'layout/general']);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const attributesForTable = chemicalAttributes.filter(
    attribute => attribute !== 'explanation_chemical_detailsearch_reference_entity_simple_select'
  );

  const handleSearchTermChange = (input: string) => {
    setSearchTerm(input);
    if (searchResultsRef?.current) {
      // remove background highlight
      searchResultsRef.current.querySelectorAll('tr').forEach(element => element.classList.remove('bg-gray-200'));

      if (!input) {
        searchResultsRef.current.scrollTop = 0;
      }
      const searchResultElement: HTMLTableRowElement | null = searchResultsRef.current.querySelector(
        `tr[data-value*="${input.toLowerCase()}"]`
      );
      if (searchResultElement) {
        searchResultElement.classList.add('bg-gray-200');
        searchResultsRef.current.scrollTop = searchResultElement.offsetTop;
      }
    }
  };

  return (
    <Section title={t('PRODUCT_DETAILS.ATTRIBUTE_GROUPS.CHEMICAL_SEARCH_TITLE')}>
      <div className="p-4">
        {
          (
            productAttributes.explanation_chemical_detailsearch_reference_entity_simple_select as LabeledAttribute<string>
          )?.value
        }
      </div>
      <div className="flex md:w-1/2 mt-4 px-4">
        <TextInput
          styles="flex-1"
          value={searchTerm}
          onKeyDown={() => {}}
          onChange={handleSearchTermChange}
          onBlur={() => {}}
          ariaLabel={t('GENERAL.ARIA_LABEL.SEARCH_INPUT', { ns: 'layout/general' })}
          placeholder={t('GENERAL.ARIA_LABEL.SEARCH', { ns: 'layout/general' })}
        />
        <IconButton
          ariaLabel={t('GENERAL.ARIA_LABEL.SEARCH_ACTION', { ns: 'layout/general' })}
          icon={IconID.SEARCH}
          size={8}
          iconColor={IconColor.WHITE}
          onClick={() => {}}
          className="bg-gray-500 hover:bg-black text-gray-100"
        />
      </div>

      <div className="mt-8 px-4 max-h-80 overflow-y-auto" ref={searchResultsRef}>
        <table className="w-full">
          <tbody>
            {attributesForTable.map(chemicalAttribute => (
              <tr
                key={chemicalAttribute}
                className="p-2 border-t border-b border-gray-100 transition-colors duration-75"
                data-value={(productAttributes[chemicalAttribute] as LabeledAttribute<string>)?.label?.toLowerCase()}
              >
                <td className="whitespace-nowrap w-0">
                  {(productAttributes[chemicalAttribute] as LabeledAttribute<string>)?.value}
                </td>
                <td className="p-4">{(productAttributes[chemicalAttribute] as LabeledAttribute<string>)?.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
};
